import {
  AnyConferenceChangedByOtherEvent,
  AnyConferenceChangedByYouEvent,
  ConferenceMemberAddedByOtherEvent,
  ConferenceMemberAddedByYouEvent,
  ConferenceMemberRemovedByOtherEvent,
  ConferenceMemberRemovedByYouEvent,
  ConferenceRenamedByOtherEvent,
  ConferenceRenamedByYouEvent,
  HistoryMessageItem,
  ConferenceMemberRoleChangedByOtherEvent,
  ConferenceMemberRoleChangedByYouEvent,
  ConferenceModeChangedByOtherEvent,
  ConferenceModeChangedByYouEvent,
  ConferenceScheduleChangedByOtherEvent,
  ConferenceScheduleUpdatedByOtherEvent,
  ConferencePublicIdentitySetByOtherEvent,
  ConferenceType,
} from '@wui/im'

import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'


import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { scheduleToTextMessage } from '@/shared/utils/schedule'
import { numberToUri, isPstnGuest, isGuest, getNumberFromPstn, toPServedUser } from '@/shared/utils/uri'

export const getSystemMessageText = (message: HistoryMessageItem<AnyConferenceChangedByOtherEvent | AnyConferenceChangedByYouEvent>): string => {
  const { findByPServed, findContactByMobilePhone } = useStoreContacts()
  const { findGuestByPserved } = useStoreRooms()
  const { currentUser } = useCurrentUser()
  const { t } = useLocalization()
  const { item: { type, executor, pServed }} = message
  const contact = findByPServed(executor)
  const isExecutorGuest = isGuest(executor)
  const targetNumber = (message as HistoryMessageItem<ConferenceMemberAddedByOtherEvent | ConferenceMemberAddedByYouEvent | ConferenceMemberRemovedByOtherEvent | ConferenceMemberRemovedByYouEvent>).item.targetNumber
  const isEventByCurrentUser = currentUser.value?.imLogin === message.item.executor && targetNumber === currentUser.value.internalNumber

  switch (type) {
  case 'ConferenceCreated': {
    if (executor && contact) {
      return t('common.conversationCreatedByNameMessage')
    }
    return t('common.conversationCreatedMessage')
  }
  case 'ConferenceMemberAddedEvent': {
    const { item: { joinBy }} = message as HistoryMessageItem<ConferenceMemberAddedByOtherEvent>

    if (joinBy) {
      if (joinBy === 'pinCode') {
        return t('chat.joinByPinCode')
      }
    }

    const addedByOther = targetNumber !== contact?.internalNumber

    if (targetNumber && isPstnGuest(targetNumber)) {
      const addedContact = findContactByMobilePhone(getNumberFromPstn(targetNumber))
      return t('chat.join', { name: addedContact?.name ?? t('common.participantWithNumber', { number: getNumberFromPstn(targetNumber) }) })
    } else if (isExecutorGuest) {
      const guestName = findGuestByPserved(pServed, executor) ?? ''
      return `${guestName} ${joinBy === 'link' ? t('chat.joinByLink') : t('chat.joiningToChat')}`
    }

    if ((targetNumber && isGuest(targetNumber)) || !addedByOther || isEventByCurrentUser) {
      return t('chat.joiningToChat')
    }

    if (currentUser.value && currentUser.value.internalNumber === targetNumber) {
      return t('chat.addYouToChat', { name: currentUser.value.name })
    }

    const addedContact = findByPServed(toPServedUser(targetNumber, false))
    if (addedContact) {
      return t('chat.join', { name: addedContact.name })
    }

    if (targetNumber) {
      return t('chat.addParticipantWithNumber', { number: targetNumber })
    }

    return t('chat.join')
  }
  case 'ConferenceMemberRemoved': {
    const deletedByOther = targetNumber !== contact?.internalNumber

    if (targetNumber && isPstnGuest(targetNumber)) {
      const deletedContact = findContactByMobilePhone(getNumberFromPstn(targetNumber))
      return t('chat.expelled', { name: deletedContact?.name ?? t('common.participantWithNumber', { number: getNumberFromPstn(targetNumber) }) })
    }

    if (targetNumber && isGuest(targetNumber)) {
      return `${t('chat.guestExpelled')} ${(message.item as ConferenceMemberRemovedByOtherEvent | ConferenceMemberRemovedByYouEvent).targetName}`
    }

    if (isEventByCurrentUser) {
      return t('chat.quitFromChat')
    }

    if (currentUser.value && currentUser.value.internalNumber === targetNumber) {
      return t('chat.removeYouFromChat', { name: currentUser.value.name })
    }

    if (!deletedByOther) {
      return t('chat.quitFromChat')
    }

    const deletedContact = targetNumber ? findByPServed(toPServedUser(targetNumber, false)) : undefined
    if (deletedContact && !executor) {
      return t('chat.temporaryParticipantWasDeleted', { name: deletedContact.name })
    }
    else if (deletedContact) {
      return t('chat.expelled', { name: deletedContact.name })
    }

    return t('chat.expelled')
  }
  case 'ConferenceRenamedEvent': {
    return t('common.newNameOfRoom', { roomName: (message as HistoryMessageItem<ConferenceRenamedByYouEvent | ConferenceRenamedByOtherEvent>).item.newName })
  }
  case 'ConferenceAvatarChangedEvent': {
    return t('common.groupAvatarHasBeenChangedBy')
  }
  case 'ConferencePinCodeDisabled': {
    return t('common.pinCodeHasBeenDisabled')
  }
  case 'ConferencePinCodeEnabled': {
    return t('common.pinCodeHasBeenCreated')
  }
  case 'ConferencePinCodeRenewed': {
    return t('common.pinCodeHasBeenChanged')
  }
  case 'ConferenceScheduleChanged': {
    const { schedule } = message.item as ConferenceScheduleChangedByOtherEvent
    return t('chat.scheduleHasBeenEnabled', {
      schedule: scheduleToTextMessage(schedule),
    })
  }
  case 'ConferenceScheduleUpdated': {
    const { schedule } = message.item as ConferenceScheduleUpdatedByOtherEvent
    return t('chat.scheduleHasBeenUpdated', {
      schedule: scheduleToTextMessage(schedule),
    })
  }
  case 'ConferenceScheduleDisabled': {
    return t('chat.scheduleHasBeenDisabled')
  }
  case 'ConferenceAutoRecordingEnabled': {
    return t('common.autoRecordingHasBeenEnabled')
  }
  case 'ConferenceAutoRecordingDisabled': {
    return t('common.autoRecordingHasBeenDisabled')
  }
  case 'ConferenceGuestAccessEnabled': {
    return t('common.guestAccessEnabledBy')
  }
  case 'ConferenceGuestAccessDisabled': {
    return t('common.guestAccessDisabledBy')
  }
  case 'ConferenceModeChangedEvent': {
    const { mode } = message.item as ConferenceModeChangedByOtherEvent | ConferenceModeChangedByYouEvent
    if (mode === ConferenceType.MODERATED) {
      return t('common.moderatedConferenceHasBeenEnabled')
    }
    return t('common.moderatedConferenceHasBeenDisabled')
  }
  case 'ConferenceRemoved': {
    if (contact || executor) {
      return t('common.chatRemovedByNameMessage')
    }
    return t('common.chatRemovedMessage')
  }
  case 'ConferenceMemberRoleChangedEvent': {
    let text = ''
    const typedItem = message.item as (ConferenceMemberRoleChangedByOtherEvent | ConferenceMemberRoleChangedByYouEvent)
    if (typedItem.targetRole === 'User') {
      text += `${t('common.removeModerator')}`
    } else {
      text += `${t('common.addModerator')}`
    }

    const targetUri = numberToUri(typedItem.targetNumber)
    const targetContact = findByPServed(targetUri)
    if (targetContact) {
      text += ` ${targetContact.name}`
    } else if (currentUser.value?.imLogin === targetUri) {
      text += ` ${currentUser.value.name}`
    } else {
      text += ` ${t('common.participantWithNumber', { number: typedItem.targetNumber })}`
    }

    return text
  }
  case 'ConferencePublicIdentitySet': {
    const { publicIdentity } = message.item as ConferencePublicIdentitySetByOtherEvent
    if (publicIdentity) {
      return t('common.publicIdentitySet', {
        publicIdentity,
      })
    } else {
      return t('common.publicIdentityRemoved')
    }
  }
  case 'ConferenceMemberRenamed':
    return t('common.renameSubscriber')
  default: {
    return type
  }
  }
}
